// FILE: modelPolicy.ts
// Purpose: The rule that decides whether a model selection is within a user's
//          model allowlist, including the managed-agent exemption.
// Layer: Shared runtime utilities
// Exports: modelPolicyKey, runsAgentOwnModel, isModelSelectionAllowed.
//
// Lives in `shared` because the server enforces this and the web app renders a
// badge from it. Two copies of the rule would mean a UI that either warns about
// a selection the server accepts, or stays quiet about one it rejects — and the
// second failure mode is only discovered by a user losing their first message.

/** The pieces of a model selection this rule needs. */
export interface ModelPolicySelection {
  readonly provider: string;
  readonly model: string;
  readonly agentId?: string | undefined;
}

/** The pieces of a managed agent this rule needs. */
export interface ModelPolicyAgent {
  readonly provider: string;
  readonly model: string;
}

/** `"provider:model"`, the shape the web app keys its allowlist set by. */
export const modelPolicyKey = (provider: string, model: string): string => `${provider}:${model}`;

/**
 * Whether the selection is the agent running the model it was configured with.
 *
 * The match is deliberately exact on both provider and model. An admin chose
 * that model when configuring the agent, so the user is not the one picking it —
 * but a selection that names a permitted agent alongside some *other* model is a
 * user-chosen model wearing an agent's name, and exempting it would launder any
 * forbidden model through any permitted agent.
 */
export const runsAgentOwnModel = (
  selection: ModelPolicySelection,
  agent: ModelPolicyAgent | null,
): boolean =>
  agent !== null && agent.provider === selection.provider && agent.model === selection.model;

/**
 * Whether the user may dispatch this selection.
 *
 * `agent` is the authorized agent the selection names — the caller resolves it,
 * because "authorized" means a database lookup on the server and a filtered list
 * on the client. Pass `null` when the selection names no agent, or names one the
 * user may not use; either way the model has to stand on the allowlist alone.
 */
export const isModelSelectionAllowed = (input: {
  readonly selection: ModelPolicySelection;
  readonly allowedModelKeys: ReadonlySet<string>;
  readonly agent: ModelPolicyAgent | null;
}): boolean =>
  runsAgentOwnModel(input.selection, input.agent) ||
  input.allowedModelKeys.has(modelPolicyKey(input.selection.provider, input.selection.model));
